import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../configs"; // sesuaikan path
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/adjustment", {
        params: {
          page,
          limit: pageSize,
          item_name: searchName || undefined,
          item_code: searchCode || undefined,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar adjustment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    // TODO: sambungkan dengan endpoint export Excel kalau sudah ada
    message.info("Fitur export Excel belum dihubungkan dengan API.");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_adjustment);
    try {
      await axiosInstance.delete(`/adjustment/${record.id_adjustment}`);
      message.success("Adjustment berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus adjustment");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO ADJUSTMENT",
      dataIndex: "adjustment_number",
      width: 130,
    },
    {
      title: "TANGGAL ADJUSTMENT",
      dataIndex: "adjustment_date",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "ALASAN ADJUSTMENT",
      dataIndex: "reason",
      width: 130,
      render: (value) => value || "-",
    },
    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Detil & Edit Adjustment">
            <Link to={`other/adjustment/update/${record.id_adjustment}`}>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                style={{
                  background: "#3699FF",
                  borderColor: "#3699FF",
                }}
              />
            </Link>
          </Tooltip>

          <Popconfirm
            title="Hapus Adjustment"
            description={`Yakin ingin menghapus "${record.adjustment_number || record.adjustment_number
              }"?`}
            okText="Ya, Hapus"
            cancelText="Batal"
            okButtonProps={{
              danger: true,
              loading: deletingId === record.id_incoming,
            }}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Hapus">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingId === record.id_incoming}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Adjustment
          </Title>
          <Text type="secondary">
            Kelola daftar adjustment untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
                fontWeight: 600,
              }}
            >
              <Link to="/other/adjustment/add" style={{ color: "#fff" }}>
                Tambah
              </Link>
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          border: "1px solid #F3F6F9",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Nama Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari Item..."
              style={{ marginTop: 4 }}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Kode Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari kode..."
              style={{ marginTop: 4 }}
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} style={{ textAlign: "right" }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
                Reset
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  background: "#8950FC",
                  borderColor: "#8950FC",
                }}
              >
                Apply Filter
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          style={{ marginTop: 36 }}
          rowKey="id_item"
          dataSource={items}
          columns={columns}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `Total ${total} data | ${range[0]}-${range[1]}`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </Card>
  );
}
